// WO-7/21 Track A — the Productivity view, built to the canonical design
// (design repo: docs/designs/2026-07-21-migration-daily-aaron-ted-angel/productivity-dashboard.html,
// Angel/ChatGPT "Productivity Tab v3"; overview updated to the Claude Design source
// productivity-tab-v3.dc.html: MiniTeam column in place of Invited/Complete, which still show in
// the drill-down pipeline). Staff-only (same viewAuditTrail gate as the roster):
// an overview of every AE grouped by team with per-preprint + reviewer-pipeline columns and a
// period filter, and a per-AE "{name}'s dashboard" drill-down with Preprints / Quality Checks /
// Students tabs. Data comes from the existing GET /dashboard/{staff,ae} endpoints (?from/?to).
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  preprintStatusLabel,
  reviewerAttentionRank,
  type AeDashboard,
  type DashboardPreprintCard,
  type StaffAeRow,
  type StaffDashboard,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, Input, Spinner, statusTone } from "../../components/ui.js";

/** Team names are countries (plus a few program hubs) — best-effort flags, 🌍 otherwise. */
const TEAM_FLAGS: Record<string, string> = {
  "United States": "🇺🇸", Berkeley: "🇺🇸", India: "🇮🇳", Nigeria: "🇳🇬", Brazil: "🇧🇷",
  Philippines: "🇵🇭", Bangladesh: "🇧🇩", Mexico: "🇲🇽", Peru: "🇵🇪", "South Africa": "🇿🇦",
  "Cape Town": "🇿🇦", Uganda: "🇺🇬", Vietnam: "🇻🇳", Ethiopia: "🇪🇹",
};
const flagFor = (team: string): string => TEAM_FLAGS[team] ?? "🌍";
const NO_TEAM = "No team assigned";

const fmtDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const initialsOf = (name: string): string => {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
};

function Avatar({ name, large = false }: { name: string; large?: boolean }): JSX.Element {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-indigo-50 font-bold text-indigo-700 ${
        large ? "h-11 w-11 text-sm" : "h-7 w-7 text-[11px]"
      }`}
    >
      {initialsOf(name)}
    </span>
  );
}

export function ProductivityPortal(): JSX.Element {
  const [from, setFrom] = useState(() => fmtDate(Date.now() - 28 * 864e5));
  const [to, setTo] = useState(() => fmtDate(Date.now()));
  const [view, setView] = useState<string | null>(null); // AE email when on the detail page

  const weeks = Math.max(0.5, Math.round(((Date.parse(to) - Date.parse(from)) / (7 * 864e5)) * 10) / 10);

  const period = (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-slate-600">Period</span>
      <Input type="date" className="w-36 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
      <span className="text-slate-400">–</span>
      <Input type="date" className="w-36 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
      <Badge tone="slate">{weeks} weeks</Badge>
    </div>
  );

  return view ? (
    <AeDetail email={view} from={from} to={to} periodControl={period} onBack={() => setView(null)} />
  ) : (
    <Overview from={from} to={to} periodControl={period} onOpen={setView} />
  );
}

// ── Overview: every AE, grouped by team ──────────────────────────────────────
function Overview({
  from,
  to,
  periodControl,
  onOpen,
}: {
  from: string;
  to: string;
  periodControl: JSX.Element;
  onOpen: (email: string) => void;
}): JSX.Element {
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const { data, isLoading } = useQuery({
    queryKey: qk.productivityStaff(from, to),
    queryFn: () => api.get<StaffDashboard>(`/dashboard/staff?from=${from}&to=${to}`),
  });

  const buckets = useMemo(() => {
    const byTeam = new Map<string, StaffAeRow[]>();
    for (const ae of data?.aes ?? []) {
      const team = ae.team?.trim() || NO_TEAM;
      byTeam.set(team, [...(byTeam.get(team) ?? []), ae]);
    }
    return [...byTeam.entries()]
      .map(([team, aes]) => ({
        team,
        aes,
        toPitch: aes.reduce((n, a) => n + a.toPitchTeam + a.toPitchEditorial, 0),
        selected: aes.reduce((n, a) => n + a.selected, 0),
        active: aes.reduce((n, a) => n + a.activeCount, 0),
        // Productivity Tab v3: MiniTeam replaces the Invited/Complete columns up here —
        // those stages still show in the drill-down's reviewer-pipeline list.
        miniTeam: aes.reduce((n, a) => n + (a.miniTeam ?? 0), 0),
        onHold: aes.reduce((n, a) => n + (a.onHold ?? 0), 0),
        dormant: aes.reduce((n, a) => n + (a.dormant ?? 0), 0),
        greenlits: aes.reduce((n, a) => n + (a.greenlitsInPeriod ?? 0), 0),
      }))
      .sort((a, b) => b.greenlits - a.greenlits);
  }, [data]);

  if (isLoading || !data) return <Spinner label="Loading productivity…" />;

  const totGreenlits = buckets.reduce((n, b) => n + b.greenlits, 0);
  const totActive = buckets.reduce((n, b) => n + b.active, 0);
  const totProposals = (data.aes ?? []).reduce((n, a) => n + a.proposals, 0);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Team / AE productivity</h1>
          <p className="mt-0.5 text-sm text-slate-500">How each Assistant Editor is doing — grouped by team. Open any AE to step in.</p>
        </div>
        {periodControl}
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Assistant Editors" value={data.aes.length} sub="Managers in roster" tone="text-amber-700" />
        <Kpi label="Greenlits in period" value={totGreenlits} sub="Reviewer invites across all AEs" tone="text-green-700" />
        <Kpi label="Active preprints" value={totActive} sub="Being shepherded" tone="text-blue-700" />
        <Kpi label="Proposals to date" value={totProposals} sub="On AE-led preprints" tone="text-violet-700" />
      </div>

      {buckets.length === 0 && <EmptyState>No Assistant Editors in the roster yet.</EmptyState>}
      {buckets.map((b) => {
        const open = !closed.has(b.team);
        return (
          <Card key={b.team} className="overflow-hidden">
            <button
              type="button"
              className="flex w-full flex-wrap items-center gap-2.5 border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50"
              aria-expanded={open}
              onClick={() =>
                setClosed((cur) => {
                  const next = new Set(cur);
                  if (next.has(b.team)) next.delete(b.team);
                  else next.add(b.team);
                  return next;
                })
              }
            >
              <span className="text-lg">{flagFor(b.team)}</span>
              <span className="min-w-[8rem]">
                <span className="block text-sm font-semibold">{b.team}</span>
                <span className="block text-xs text-slate-500">
                  {b.aes.length} Assistant Editor{b.aes.length === 1 ? "" : "s"}
                </span>
              </span>
              <span className="flex flex-wrap gap-1.5">
                <Badge tone="blue">To-Pitch {b.toPitch}</Badge>
                <Badge tone="violet">Selected {b.selected}</Badge>
                <Badge tone="blue">Active {b.active}</Badge>
                <Badge tone="violet">MiniTeam {b.miniTeam}</Badge>
                <Badge tone="amber">On Hold {b.onHold}</Badge>
                <Badge tone="slate">Dormant {b.dormant}</Badge>
              </span>
              <span className="ml-auto flex items-center gap-3">
                <span className="text-right">
                  <span className="block text-lg font-bold leading-tight text-green-700">{b.greenlits}</span>
                  <span className="block text-[10px] uppercase tracking-wide text-slate-500">Greenlits</span>
                </span>
                <span className="text-xs text-slate-400">{open ? "▲" : "▼"}</span>
              </span>
            </button>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[60rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="border-b border-slate-100 px-4 py-2">AE</th>
                      <th className="border-b border-slate-100 px-2 py-2">Domains</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">To-Pitch</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Selected</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Active</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">MiniTeam</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">On Hold</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Dormant</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-right">Greenlits</th>
                      <th className="border-b border-slate-100 px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {b.aes.map((ae) => (
                      <tr
                        key={ae.email}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => onOpen(ae.email)}
                      >
                        <td className="border-b border-slate-100 px-4 py-2">
                          <span className="flex items-center gap-2">
                            <Avatar name={ae.name ?? ae.email} />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium">{ae.name ?? ae.email}</span>
                              <span className="block truncate text-xs text-slate-400">{ae.email}</span>
                            </span>
                          </span>
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2 text-xs text-slate-500">
                          {ae.domains.join(" · ") || "—"}
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2 text-center">
                          <Badge tone="blue">T {ae.toPitchTeam}</Badge>{" "}
                          <Badge tone="amber">E {ae.toPitchEditorial}</Badge>
                        </td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center">{ae.selected}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center">{ae.activeCount}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-violet-700">{ae.miniTeam ?? 0}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-slate-500">{ae.onHold ?? 0}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-slate-500">{ae.dormant ?? 0}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-right font-semibold text-green-700">
                          {ae.greenlitsInPeriod ?? 0}
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-4 py-2 text-right text-sm font-medium text-brand">
                          Open →
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: number; sub: string; tone: string }): JSX.Element {
  return (
    <Card>
      <CardBody className="space-y-1">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`text-3xl font-bold ${tone}`}>{value}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </CardBody>
    </Card>
  );
}

// ── Detail: "{name}'s dashboard" with Preprints / Quality Checks / Students ──
type Tab = "pre" | "qc" | "stu";

function AeDetail({
  email,
  from,
  to,
  periodControl,
  onBack,
}: {
  email: string;
  from: string;
  to: string;
  periodControl: JSX.Element;
  onBack: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<Tab>("pre");
  const { data, isLoading } = useQuery({
    queryKey: qk.productivityAe(email, from, to),
    queryFn: () => api.get<AeDashboard>(`/dashboard/ae?editor=${encodeURIComponent(email)}&from=${from}&to=${to}`),
  });

  if (isLoading || !data) return <Spinner label="Loading AE dashboard…" />;
  const name = data.editor.name ?? data.editor.email;
  const team = data.editor.team?.trim();

  return (
    <div className="space-y-5">
      <Button variant="ghost" onClick={onBack}>
        ← Back to Productivity overview
      </Button>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar name={name} large />
          <div>
            <h1 className="text-2xl font-bold">{name}&rsquo;s dashboard</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {data.editor.email}
              {team && ` · ${flagFor(team)} ${team}`}
              {(data.editor.domains ?? []).length > 0 && ` · ${(data.editor.domains ?? []).join(" · ")}`}
            </p>
          </div>
        </div>
        {periodControl}
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Greenlits in period" value={data.kpis.greenlitsInPeriod ?? 0} sub="Invited reviewers in the period" tone="text-green-700" />
        <Kpi label="Greenlits to date" value={data.kpis.greenlitsToDate} sub="On preprints they lead" tone="text-green-700" />
        <Kpi label="Proposals" value={data.kpis.proposals} sub="To date (truly proposed)" tone="text-blue-700" />
        <Kpi label="Greenlit proposals" value={data.kpis.greenlitProposals} sub="Reached Invite" tone="text-violet-700" />
      </div>

      <div className="flex gap-1">
        {(
          [
            ["pre", "Preprints"],
            ["qc", "Quality Checks"],
            ["stu", "Students"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === value ? "bg-brand/10 text-brand" : "text-slate-600 hover:bg-slate-100"}`}
            aria-pressed={tab === value}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "pre" && <PreprintsTab d={data} />}
      {tab === "qc" && <QcTab d={data} />}
      {tab === "stu" && <StudentsTab d={data} />}

      {data.viewingOther && (
        <p className="text-xs text-slate-400">
          <Badge tone="amber">Viewing</Badge> You are viewing {data.editor.email}. Actions still respect your own permissions.
        </p>
      )}
    </div>
  );
}

function PreCardRow({ c, showMeta = false }: { c: DashboardPreprintCard; showMeta?: boolean }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 p-2">
      <span className="truncate text-sm">{c.title}</span>
      {showMeta && (
        <span className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
          {c.miniTeamCount != null && <span title="Mini-team members">👥 {c.miniTeamCount}</span>}
          <span className="text-sm font-semibold text-green-700">{c.greenlitCount ?? 0}</span>
        </span>
      )}
    </div>
  );
}

function PreprintsTab({ d }: { d: AeDashboard }): JSX.Element {
  const toPitch = d.preprints.toPitchTeam.length + d.preprints.toPitchEditorial.length;
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">To-Pitch</h3>
            <Badge tone="slate">{toPitch}</Badge>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="space-y-1.5">
              <span className="flex items-center gap-2">
                <Badge tone="blue">Team</Badge>
                <span className="text-xs text-slate-400">{d.preprints.toPitchTeam.length}</span>
              </span>
              <div className="space-y-1.5 border-l-2 border-blue-400 pl-2">
                {d.preprints.toPitchTeam.map((c) => (
                  <PreCardRow key={c.selectedId ?? c.title} c={c} />
                ))}
              </div>
              <span className="flex items-center gap-2">
                <Badge tone="amber">Editorial</Badge>
                <span className="text-xs text-slate-400">{d.preprints.toPitchEditorial.length}</span>
              </span>
              <div className="space-y-1.5 border-l-2 border-amber-400 pl-2">
                {d.preprints.toPitchEditorial.map((c) => (
                  <PreCardRow key={c.selectedId ?? c.title} c={c} />
                ))}
              </div>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Selected</h3>
            <Badge tone="violet">{d.preprints.selected.length}</Badge>
          </CardHeader>
          <CardBody className="space-y-1.5">
            {d.preprints.selected.map((c) => (
              <PreCardRow key={c.selectedId ?? c.title} c={c} />
            ))}
            {d.preprints.selected.length === 0 && <p className="text-xs text-slate-400">None.</p>}
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Active</h3>
            <span className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">Greenlits</span>
              <Badge tone="green">{d.preprints.active.reduce((n, c) => n + (c.greenlitCount ?? 0), 0)}</Badge>
            </span>
          </CardHeader>
          <CardBody className="space-y-1.5">
            {d.preprints.active.map((c) => (
              <PreCardRow key={c.preprintId ?? c.title} c={c} showMeta />
            ))}
            {d.preprints.active.length === 0 && <p className="text-xs text-slate-400">None.</p>}
          </CardBody>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">
            Reviewer pipeline — Pending Reviewer Responses / In Queue / Invited / Complete
          </h3>
        </CardHeader>
        <CardBody className="space-y-1">
          {(d.pipeline ?? []).length === 0 && <p className="text-xs text-slate-400">Nothing past reviewer selection yet.</p>}
          {(d.pipeline ?? []).map((c) => (
            <div key={c.preprintId ?? c.title} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1.5 last:border-b-0">
              <span className="truncate text-sm">{c.title}</span>
              <Badge tone={statusTone(c.status)}>{preprintStatusLabel(c.status)}</Badge>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}

function QcTab({ d }: { d: AeDashboard }): JSX.Element {
  const rows = d.qcDecisions ?? [];
  const counts = new Map<string, number>();
  for (const q of rows) counts.set(q.status, (counts.get(q.status) ?? 0) + 1);
  const ordered = [...counts.entries()].sort(
    (a, b) => reviewerAttentionRank(a[0] as never) - reviewerAttentionRank(b[0] as never) || a[0].localeCompare(b[0]),
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {ordered.map(([status, n]) => (
          <Badge key={status} tone={statusTone(status)}>
            {status} {n}
          </Badge>
        ))}
        {rows.length === 0 && <p className="text-xs text-slate-400">No QC decisions in this period.</p>}
      </div>
      {rows.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="w-28 border-b border-slate-100 px-4 py-2">Date of QC</th>
                <th className="border-b border-slate-100 px-2 py-2">Reviewer</th>
                <th className="border-b border-slate-100 px-2 py-2">Preprint</th>
                <th className="w-36 border-b border-slate-100 px-4 py-2 text-right">Status set</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q, i) => (
                <tr key={`${q.reviewer}-${i}`} className="hover:bg-slate-50">
                  <td className="border-b border-slate-100 px-4 py-2 text-slate-500">{q.date ?? "—"}</td>
                  <td className="border-b border-slate-100 px-2 py-2 font-medium">{q.reviewer}</td>
                  <td className="max-w-96 truncate border-b border-slate-100 px-2 py-2 text-slate-600">{q.preprintTitle}</td>
                  <td className="border-b border-slate-100 px-4 py-2 text-right">
                    <Badge tone={statusTone(q.status)}>{q.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <p className="text-xs text-slate-400">
        The overview counts greenlits only (QCs that reached Invite); this list shows every quality-check decision in the period.
      </p>
    </div>
  );
}

function StudentsTab({ d }: { d: AeDashboard }): JSX.Element {
  return (
    <div className="space-y-2">
      <Card className="overflow-hidden">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="border-b border-slate-100 px-4 py-2">Student</th>
              <th className="w-40 border-b border-slate-100 px-2 py-2 text-right">Domain / team</th>
              <th className="w-24 border-b border-slate-100 px-2 py-2 text-right">Proposals</th>
              <th className="w-24 border-b border-slate-100 px-4 py-2 text-right">Greenlits</th>
            </tr>
          </thead>
          <tbody>
            {d.students.map((s) => (
              <tr key={s.email} className="hover:bg-slate-50">
                <td className="border-b border-slate-100 px-4 py-2">
                  {s.name ?? s.email}
                  {s.name && <span className="ml-1.5 text-xs text-slate-400">{s.email}</span>}
                </td>
                <td className="border-b border-slate-100 px-2 py-2 text-right text-xs text-slate-500">
                  {[s.domains.join("/"), s.team].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="border-b border-slate-100 px-2 py-2 text-right">{s.proposals}</td>
                <td className="border-b border-slate-100 px-4 py-2 text-right font-semibold text-green-700">{s.greenlits}</td>
              </tr>
            ))}
            {d.students.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-sm text-slate-400">
                  No students have proposed on this AE&rsquo;s preprints yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-400">Students who proposed reviewers on preprints this AE leads.</p>
    </div>
  );
}
